import { CreditCard, ScanLine, IdCard, DoorOpen, Wallet, Globe, ArrowRight } from "lucide-react";
import { Reveal } from "../smart/Reveal";
import { CHANGE_CARDS, THREE_ROLES, ROLLOUT_ENTRANCES, ROLLOUT_EXTEND, ASSETS } from "../../data/arccaContent";

const ICONS = { CreditCard, ScanLine, IdCard, DoorOpen, Wallet, Globe };

const Eyebrow = ({ children }) => (
  <p className="text-xs font-display font-bold uppercase tracking-[0.2em] text-arcca-dark">{children}</p>
);

/* ---------- Ce se schimbă la Arcca ---------- */
export const ArccaChange = () => (
  <section id="ce-se-schimba" data-testid="arcca-change" className="scroll-mt-16 bg-white py-16 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-3xl">
        <Reveal><Eyebrow>Ce se schimbă la Arcca</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-arccaink text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
            Arcca introduce o experiență de acces mai simplă pentru studenți.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Arcca modernizează treptat accesul în campus prin integrarea BRD-ISIC. Asta înseamnă că același
            card pe care îl folosești pentru plăți și beneficii ISIC va putea fi folosit și pentru accesul prin
            turnicheții compatibili. Tranziția se face pas cu pas, astfel încât accesul în campus să rămână
            simplu și funcțional pe tot parcursul implementării.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {CHANGE_CARDS.map((card, i) => {
          const Icon = ICONS[card.icon];
          return (
            <Reveal key={card.title} delay={i * 0.1}>
              <div
                data-testid={`change-card-${i}`}
                className="group h-full rounded-3xl border border-slate-200 bg-white p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-arccaink/5 hover:border-arcca/40 transition-[transform,box-shadow,border-color] duration-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-arcca/12 text-arcca-dark">
                  <Icon size={22} strokeWidth={2.1} />
                </span>
                <h3 className="mt-5 font-display font-bold text-xl text-arccaink">{card.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{card.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- Un card, trei roluri ---------- */
export const ArccaThreeRoles = () => (
  <section id="trei-roluri" data-testid="arcca-threeroles" className="scroll-mt-16 bg-slate-50 py-16 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-3xl">
        <Reveal><Eyebrow>Un card, trei roluri</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-arccaink text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
            Un card pentru campus, plăți și beneficii.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            BRD-ISIC aduce într-un singur card lucrurile de care ai nevoie zi de zi ca student: acces mai simplu
            în campus, funcționalități bancare BRD și legitimația internațională ISIC, cu reduceri în România și
            în străinătate.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="relative flex items-center justify-center rounded-[2rem] bg-gradient-to-br from-arcca/15 via-white to-arccablue/10 p-8 lg:p-12">
            <img
              src={ASSETS.card}
              alt="Cardul BRD-ISIC"
              className="w-full max-w-sm rounded-2xl shadow-2xl shadow-arccaink/20 ring-1 ring-black/5 rotate-[-4deg]"
            />
          </div>
        </Reveal>

        <div className="lg:col-span-7 space-y-4">
          {THREE_ROLES.map((role, i) => {
            const Icon = ICONS[role.icon];
            return (
              <Reveal key={role.tag} delay={0.15 + i * 0.1}>
                <div
                  data-testid={`role-${i}`}
                  className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-6 hover:border-arcca/40 transition-colors"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-arcca/12 text-arcca-dark">
                    <Icon size={22} strokeWidth={2.1} />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-lg text-arccaink">{role.tag}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600">{role.text}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Noul sistem se activează treptat ---------- */
const Turnstiles = ({ total, active }) => (
  <div className="mt-6 flex items-end gap-2.5" aria-hidden="true">
    {Array.from({ length: total }).map((_, i) => {
      const on = i < active;
      return (
        <div key={i} className="flex flex-col items-center gap-1.5">
          <div
            className={`h-14 w-8 rounded-lg border-2 ${
              on ? "border-arcca bg-arcca/15" : "border-slate-200 bg-slate-100"
            } flex items-center justify-center`}
          >
            <ScanLine size={15} className={on ? "text-arcca-dark" : "text-slate-300"} strokeWidth={2.2} />
          </div>
          <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-arcca" : "bg-slate-300"}`} />
        </div>
      );
    })}
  </div>
);

export const ArccaRollout = () => (
  <section id="implementare" data-testid="arcca-rollout" className="scroll-mt-16 bg-white py-16 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-3xl">
        <Reveal><Eyebrow>Pas cu pas</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-arccaink text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
            Noul sistem se activează treptat.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Pentru început, BRD-ISIC va putea fi folosit la o parte dintre turnicheții existenți. Sistemul actual
            rămâne disponibil în paralel în prima etapă, iar Arcca va anunța studenții pe măsură ce noua soluție
            se extinde.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        {ROLLOUT_ENTRANCES.map((e, i) => (
          <Reveal key={e.title} delay={i * 0.1}>
            <div data-testid={`rollout-entrance-${i}`} className="h-full rounded-3xl border border-slate-200 bg-slate-50 p-8">
              <h3 className="font-display font-bold text-xl text-arccaink">{e.title}</h3>
              <Turnstiles total={e.total} active={e.active} />
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 text-xs font-medium">
                <span className="inline-flex items-center gap-1.5 text-arcca-dark">
                  <span className="h-2.5 w-2.5 rounded-sm bg-arcca/25 ring-2 ring-arcca" /> Compatibil BRD-ISIC
                </span>
                <span className="inline-flex items-center gap-1.5 text-slate-400">
                  <span className="h-2.5 w-2.5 rounded-sm bg-slate-100 ring-2 ring-slate-200" /> Sistem actual
                </span>
              </div>
              <p className="mt-5 text-[15px] leading-relaxed text-slate-600">{e.text}</p>
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.2}>
          <div data-testid="rollout-extend" className="h-full rounded-3xl bg-arccaink p-8 text-white">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-arcca/20 text-arcca">
              <ArrowRight size={22} strokeWidth={2.2} />
            </span>
            <h3 className="mt-5 font-display font-bold text-xl">{ROLLOUT_EXTEND.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-white/70">{ROLLOUT_EXTEND.text}</p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <p className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 px-6 py-5 text-sm leading-relaxed text-slate-500">
          Vei primi din partea Arcca toate detaliile importante despre activarea cititoarelor și folosirea
          cardului pentru acces.
        </p>
      </Reveal>
    </div>
  </section>
);
